/**
 * @see ./tableOfContentsInjector.spec.md for behavioral specification
 */

/** @typedef {import('../components/tocContainer.js').TocContainer} TocContainer */

/**
 * Set standard attributes on a toc-container element
 * @param {TocContainer} tableOfContents
 * @param {string} selector
 */
function setTocAttributes(tableOfContents, selector) {
    tableOfContents.setAttribute('top-level', '2');
    tableOfContents.setAttribute('bottom-level', '4');
    tableOfContents.setAttribute('depth', '3');
    tableOfContents.setAttribute('open', 'true');
    tableOfContents.setAttribute('selector', selector);
}

/**
 * @param {HTMLElement} target HTML element to be targeted of table of content injection
 * @param {string} selector CSS selector for which to scope the table of contents
 * @param {number} [targetHeaderCount] Number of heading before which table of contents should appear
 * @returns {HTMLElement|undefined} The injected table of contents element, or undefined if no table of contents was injected
 */
export function injectTableOfContents(target, selector, targetHeaderCount = 1) {
    // If a toc-container already exists, update its attributes and exit
    const existingToc = target.querySelector('toc-container');
    if (existingToc) {
        return;
    }

    if (!selector) {
        throw new Error('Selector is required');
    }

    const queryArea = target.querySelector(selector) || target;
    const h2Headings = queryArea.querySelectorAll('h2');


    // Only inject ToC if there are 4 or more H2 headings
    if (h2Headings.length < 4) {
        return;
    }

    const tableOfContents = document.createElement('toc-container');
    setTocAttributes(tableOfContents, selector);

    // Lazy-load the toc-container web component if not already registered.
    // app.js conditionally loads it for server-rendered elements, but when the element
    // is created dynamically (like here), app.js may have already checked and found nothing.
    // customElements.get() prevents loading the module twice.
    if (!customElements.get('toc-container')) {
        import('../components/tocContainer.js');
    }

    const targetHeading = h2Headings[targetHeaderCount - 1];
    targetHeading.parentElement.insertBefore(tableOfContents, targetHeading);

    return tableOfContents;
}
